import i18n from '@/i18n'
import { apiClient } from 'Services/api'
import endpoints from 'Services/endpoints'
import { objectHasPropertyValue } from 'Utils'
import { checkErrorResponse } from 'Services/helpers'
import { Notification } from 'Utils/Classes/Notification'

// Preparing notification error
const titleError = new Error('El título no puede estar vacío')
titleError.name = 'empty-title'
const notification = new Notification()

export default {
  // CARD ACTIONS
  get: {
    async card(id) {
      try {
        const card = await apiClient.get(endpoints.cards.getCard(id))
        return card
      } catch (error) {
        const notification = new Notification()
        if (!error.response) {
          notification.message = i18n.t('ui.notifications.error.common')
          notification.dispatch('notifications/add', notification)
        }
        throw error
      }
    },

    async cards() {
      try {
        const cards = await apiClient.get(endpoints.cards.store())
        return cards
      } catch (error) {
        if (error.response) {
          const notification = new Notification()
          notification.message = i18n.t('ui.notifications.error.common')
          notification.dispatch('notifications/add', notification)
        }
        throw error
      }
    },
    userCards() {
      return apiClient.get(endpoints.cards.userCards())
    },
  },

  update: {
    async card(id, newValues) {
      try {
        const update = await apiClient.patch(endpoints.cards.getCard(id), newValues)
        return update
      } catch (error) {
        const notification = new Notification()
        notification.message = i18n.t('ui.notifications.error.common')
        notification.dispatch('notifications/add', notification)
        throw error
      }
    },
  },

  // CARD's CHECKLIST ACTIONS
  checklists: {
    /**
     *  Create
     *
     * @param {Nuber | String} cardId The template that will hold the new checklist.
     * @param {Object} checklistInfo Checklist's required initial info.
     * @param {String} checklistInfo.title Checklist's title.
     * @param {Number} checklistInfo.order Checklist's position.
     * @returns {Object} - success: new checklist data or error api response.
     */
    async create(cardId, checklistInfo) {
      try {
        const newChecklist = await apiClient.post(
          endpoints.cards.cardChecklists(cardId),
          checklistInfo
        )
        return newChecklist
      } catch (error) {
        // 'title' field can't be empty
        if (!objectHasPropertyValue(checklistInfo, 'title')) {
          notification.message = `El título no puede estar vacío`
        } else {
          notification.message = `No se ha podido crear el checklist: ${checkErrorResponse(error)}`
        }
        notification.dispatch()
        return error
      }
    },
    /**
     * This function will update a given checklist.
     * @param {String | Number} cardId The card where the ckecklist is.
     * @param {String | Number} checklistId The checklist to update.
     * @param { Object } newValues Values to update: Only 'title', 'description' and 'is_active'.
     * @returns { Object } Success or error api response.
     */
    async update(cardId, checklistId, newValues) {
      try {
        return await apiClient.patch(endpoints.cards.cardChecklist(cardId, checklistId), newValues)
      } catch (error) {
        // Checklist's title can't be empty
        if (!objectHasPropertyValue(newValues, 'title')) {
          notification.message = titleError
        } else {
          notification.message = `No se ha podido actualizar el checklist: ${checkErrorResponse(
            error
          )}`
        }
        notification.dispatch()
        return error
      }
    },

    /**
     * Delete
     *
     * @param {String | Number} cardId The card that holds the checklist to delete.
     * @param {String | Number} checklistId Checklist to delete.
     * @returns error api response if fails.
     */
    async delete(cardId, checklistId) {
      try {
        const request = await apiClient.delete(endpoints.cards.cardChecklist(cardId, checklistId))
        return request
      } catch (error) {
        notification.type = 'error'
        notification.message = `No se ha podido eliminar el checklist: ${checkErrorResponse(error)}`
        notification.dispatch()
        return error
      }
    },

    // ITEMS ACTIONS
    items: {
      /**
       *  Create
       *
       * @param {Number | String} cardId The template that hold's the new item's checklist.
       * @param {Number | String} checklistId The new item's checklist.
       * @param {Object} newItemInfo The Item's required initial info.
       * @param {String} newItemInfo.title Item's spanish title.
       * @param {Number} newItemInfo.order Item's position.
       * @returns {Object} newItem or error api response.
       */
      async create(cardId, checklistId, newData) {
        try {
          if (!objectHasPropertyValue(newData, 'title')) throw titleError
          const newItem = await apiClient.post(
            endpoints.cards.cardChecklistItems(cardId, checklistId),
            newData
          )
          return newItem
        } catch (error) {
          // 'title' field can't be empty
          notification.type = 'error'
          if (error.name === 'empty-title') {
            notification.message = error.message
          } else {
            notification.message = `No se ha podido crear la tarea: ${checkErrorResponse(error)}`
          }
          notification.dispatch()
          return error
        }
      },

      /**
       * Update
       *
       * @param {Object} ItemInfo Information about current item to update.
       * @param {String | Number} ItemInfo.cardId The card that holds the item's checklist.
       * @param {String | Number} ItemInfo.checklistId The item's checklist.
       * @param {String | Number} ItemInfo.itemId The item to update.
       * @param {Object} ItemInfo.newData New fields to update: 'title', 'order', 'type', 'time',  'is_completed'.
       * @returns { Object } Success or error api response.
       */
      async update({ cardId, checklistId, itemId, newData }) {
        try {
          if (!objectHasPropertyValue(newData, 'title')) throw titleError
          return await apiClient.patch(
            endpoints.cards.cardChecklistItem(cardId, checklistId, itemId),
            newData
          )
        } catch (error) {
          // Item's title can't be empty
          if (!objectHasPropertyValue(newData, 'title')) {
            notification.message = error.message
          } else {
            notification.message = `No se ha podido actualizar la tarea: ${checkErrorResponse(
              error
            )}`
          }
          notification.dispatch()
          return error
        }
      },

      /**
       * Delete
       *
       * @param {String | Number} cardId Template that holds the checklist.
       * @param {String | Number} checklistId Checklist to delete.
       * * @param {String | Number} itemId Item to delete.
       * @returns error api response if fails.
       */
      async delete(cardId, checklistId, itemId) {
        // console.log(endpoints.cards.cardChecklistItem(cardId, checklistId, itemId))
        try {
          await apiClient.delete(endpoints.cards.cardChecklistItem(cardId, checklistId, itemId))
        } catch (error) {
          notification.type = 'error'
          notification.message = `No se ha podido eliminar el item: ${checkErrorResponse(error)}`
          notification.dispatch()
          return error
        }
      },
    },
  },

  // CARD's ACTIVITIES
  activity: {
    /**
     * Create Comment
     *
     * @param {String|Number} cardId Comment's card id.
     * @param {String} comment The string comment to add.
     *
     * @returns {Object} New comment data or error response.
     */
    async createComment(cardId, comment) {
      try {
        if (!comment) return
        const newComment = await apiClient.post(endpoints.cards.cardActivities(cardId), {
          message: comment,
        })
        return newComment
      } catch (error) {
        return error
      }
    },

    /**
     * Update Comment
     *
     * @param {String|Number} cardId Comment's card id.
     * @param {String|Number} activityId The comment's id.
     * @param {String} comment Updated comment string.
     *
     * @returns {Object} Updated comment data or error response.
     */
    async updateComment(cardId, activityId, comment) {
      try {
        if (!comment) return
        const newComment = await apiClient.patch(endpoints.cards.cardActivity(cardId, activityId), {
          message: comment,
        })
        return newComment
      } catch (error) {
        return error
      }
    },

    /**
     * Delete Comment
     *
     * @param {String|Number} cardId Comment's card id.
     * @param {String|Number} activityId The comment's id.
     *
     * @returns {Object} Updated comment data or error response.
     */
    async deleteComment(cardId, activityId) {
      try {
        const newComment = await apiClient.delete(endpoints.cards.cardActivity(cardId, activityId))
        return newComment
      } catch (error) {
        return error
      }
    },
  },
}
